import { memo, useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LoadingFallback from '../components/elements/LoadingFallback';
import { useSelector, useDispatch } from 'react-redux';
import { loadUserData, determineUserDestination } from '../util/navigationUtils';
import { withBookTag, currentUrlBookTag } from '../util/bookTag';
import { io } from 'socket.io-client';
import {
  setCoverImages,
  setImages,
  addImage,
} from '../redux/slice/sketchSlice';
import {
  setCoverImageUploadStatus,
  setImageBatchUploadStatus,
  clearUploadingImages,
  setIsComingFromNonUserRoute,
  setImageUploadStatus,
  setShouldWarmupLineart,
  setShouldWarmupCover,
  setLineartProgressByBook,
  setForceBookRefresh
} from '../redux/slice/stepSlice';
import { fetchBatchImagesByBookId } from '../services/mediaService';
import { fetchOrderedInteriorImages } from '../util/bookPageOrderUtils';
import { updateBook, selectBooksLoadedAt } from '../redux/slice/profileSlice';
import { SOCKET_URL } from '../config/apiConfig';
import { loadPendingImages, clearPendingImages, clearPendingCoverImage, hasImagesSentToRunPending, clearExpectedInteriorPageCount, setExpectedInteriorPageCount, resolveLineartProgressTotalImages } from '../util/pendingImageStorage';
import {
  countFinishedInteriorFromBatchRows,
  shouldClearLineartStorageAfterJob,
} from '../util/previewInteriorGridUtils';
import { fetchStagingUploadStatus } from '../services/stagingUploadService';
import { syncLocalCartToDB, getShopifyCart, pruneSelectedPlanLocalStorage, loadCartBookIds } from '../services/shopifyCartService';
import { rememberPdfOffer, hasPendingPdfOffer, pdfOfferStorePage } from '../pages/front/account/PdfOffer';
import { isEtsyFinished, isEtsySession } from '../services/etsyService';
import { giftIdFromSearch } from '../services/giftService';
import { markLineartRunFinished } from '../util/lineartRunFinished';

function shouldClearLineartLocalStorage(
  bookId,
  batchRows,
  { activeBook, lineartProgress, useStagingCountCheck = false } = {},
) {
  return shouldClearLineartStorageAfterJob(bookId, batchRows, {
    activeBook,
    lineartProgress,
    useStagingCountCheck,
  });
}

/** Paths where we should not override the URL after auth (post-login “smart” redirect). */
const USER_NO_SMART_REDIRECT_PATHS = new Set([
  '/my-orders',
  '/finalize-order',
  '/pdf-offer',
  '/shopify-cart',
  '/help',
  '/gift/welcome',
  '/gift/plan',
  '/gift/books',
  '/gift/done',
]);

const UserRoute = memo(function UserRoute({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { session } = useSelector((state) => state?.authProvider);
  const { booksLoading = false, myBooks = [] } = useSelector((state) => state?.profile);
  const booksLoadedAt = useSelector(selectBooksLoadedAt);
  const { activeBookId, isComingFromNonUserRoute, fetchingImages, lineartProgressByBook } = useSelector((state) => state?.stepFlow);
  const [hasRedirected, setHasRedirected] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [userData, setUserData] = useState(null);

  //  Track lineart job completion
  const lineartJobRef = useRef({
    isActive: false,
    jobId: null,
    expectedTotal: 0,
    receivedCount: 0
  });

  // Track latest lineartProgressByBook for use inside socket closures
  const lineartProgressRef = useRef(lineartProgressByBook);
  const myBooksRef = useRef(myBooks);

  // Race protection: discard stale fetch responses in socket handlers
  const fetchSequenceRef = useRef(0);

  //  Track cover job completion
  const coverJobRef = useRef({
    isActive: false,
    jobId: null,
    expectedTotal: 0,
    receivedCount: 0
  });

  const stagingUploadEnabledRef = useRef(false);

  useEffect(() => {
    fetchStagingUploadStatus()
      .then((status) => {
        stagingUploadEnabledRef.current = !!(status?.enabled && status?.configured);
      })
      .catch(() => {
        stagingUploadEnabledRef.current = false;
      });
  }, []);

  // Keep lineartProgressRef in sync with latest Redux state
  useEffect(() => {
    lineartProgressRef.current = lineartProgressByBook;
  }, [lineartProgressByBook]);

  useEffect(() => {
    myBooksRef.current = myBooks;
  }, [myBooks]);

  // The cart sync only cares about which books exist and whether they are already ordered.
  // `myBooks` itself is a fresh array on every line-art progress update (several a second during
  // generation), which used to re-fire the sync effect that often.
  const cartRelevantBooksKey = useMemo(
    () =>
      myBooks
        .map((b) => `${b?._id}:${b?.isOrderPlaces ? 1 : 0}:${b?.lastOrderCreatedAt || ''}`)
        .join('|'),
    [myBooks],
  );

  // Handle book warmup
  useEffect(() => {
    return () => {
      dispatch(setShouldWarmupCover(!activeBookId));
      dispatch(setShouldWarmupLineart(!activeBookId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBookId])

  // Handle authentication redirects
  useEffect(() => {
    if (session?.status === 'unauthorized') {
      // Only redirect to signin if the token is genuinely gone.
      // If token still exists in localStorage, this is likely a transient verify failure
      // (network hiccup, server busy, race condition) — don't redirect.
      const tokenExists = !!localStorage.getItem('auth_token');
      if (!tokenExists) {
        // An Etsy buyer who just finished and pressed Back has no account to
        // sign in to; the sign-in page would bounce them to Shopify.
        if (isEtsyFinished()) {
          navigate('/etsy/done?expired=1', { replace: true });
          return;
        }
        // The PDF downsell link, signed out: the store's /pages/pdf-offer opens Shopify's
        // sign-in and hands back to /pdf-offer. The remembered visit covers any other way in.
        if (window.location.pathname === '/pdf-offer') {
          rememberPdfOffer();
          const storePage = pdfOfferStorePage();
          if (storePage) {
            window.location.replace(storePage);
            return;
          }
        }
        navigate('/signin');
      }
      return;
    }

    if (session?.status === 'authorized' && session?.user?.role === 'admin') {
      navigate('/admin');
      return;
    }
  }, [session?.status, session?.user?.role, navigate]);

  // Load user data when authorized
  useEffect(() => {
    const fetchUserData = async () => {
      if (
        session?.status === 'authorized' &&
        session?.user?.role === 'user' &&
        session?.user?._id &&
        !isLoadingData &&
        !userData
      ) {
        setIsLoadingData(true);
        try {
          const data = await loadUserData(
            session.user._id,
            dispatch,
            isComingFromNonUserRoute
          );
          setUserData(data);

          // If the active book has pending images in localStorage, ensure the UI shows as processing
          if (data?.activeBookId) {
            const pending = loadPendingImages(data.activeBookId);
            if (pending.length > 0) {
              console.log(' [IMAGE_DEBUG] Restoring processing status from localStorage on mount');
              dispatch(setImageBatchUploadStatus(true));
              dispatch(setLineartProgressByBook({
                bookId: data.activeBookId,
                isProcessing: true,
                overallPercentage: 5, // Minimum progress to show the bar
                currentStage: 'ai_processing'
              }));
            }
          }
        } catch (error) {
          console.error('Error loading user data:', error);
        } finally {
          setIsLoadingData(false);
        }
      }
    };

    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?._id, isComingFromNonUserRoute, isLoadingData, userData?._id]);

  // Handle cart sync
  useEffect(() => {
    if (session?.status === 'authorized' && session?.user?.role === 'user') {
      // Not just "done loading": the list must have loaded, and for this customer.
      if (!booksLoadedAt) return;

      const syncCart = async () => {
        try {
          if (window.__DID_COOKIE_MERGE__) {
            console.log("Cookie merge detected, allowing background sync to DB...");
            return;
          }

          pruneSelectedPlanLocalStorage(myBooksRef.current, booksLoadedAt);

          const readLocalCartLength = () => {
            try {
              const raw = localStorage.getItem('selectedPlan');
              if (!raw) return 0;
              const parsed = JSON.parse(raw);
              const arr = Array.isArray(parsed) ? parsed : parsed ? [parsed] : [];
              return arr.filter(Boolean).length;
            } catch {
              return 0;
            }
          };

          const localCount = readLocalCartLength();

          // If the user already has a cart in this browser, treat it as source of truth and
          // push it to the server. Do not overwrite with DB first — stale or duplicate server
          // rows (e.g. after opening Shopify checkout then using Back) would corrupt the cart.
          if (localCount > 0) {
            console.log('Cart sync: pushing local selectedPlan to DB (skipping DB→local overwrite)');
            await syncLocalCartToDB();
            window.dispatchEvent(new Event('cartUpdated'));
            return;
          }

          console.log('Cart sync: no local cart — hydrating selectedPlan from DB');
          const dbCartRes = await getShopifyCart();

          if (dbCartRes?.data && Array.isArray(dbCartRes.data)) {
            if (dbCartRes.data.length > 0) {
              localStorage.setItem('selectedPlan', JSON.stringify(dbCartRes.data));
            } else {
              localStorage.removeItem('selectedPlan');
            }
            window.dispatchEvent(new Event('cartUpdated'));
            console.log('Cart local storage updated from DB successfully');
          }
        } catch (error) {
          console.error("Error executing cart sync check in UserRoute:", error);
        }
      };

      syncCart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.status, session?.user?.role, booksLoadedAt, cartRelevantBooksKey]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const stepCode = searchParams.get('stepCode');

    if (window.location.pathname === '/thank-you') {
      setHasRedirected(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname]);

  // Perform smart redirect once data is loaded
  useEffect(() => {
    if (
      session?.status === 'authorized' &&
      session?.user?.role === 'user' &&
      userData &&
      !hasRedirected &&
      !booksLoading
    ) {
      if (USER_NO_SMART_REDIRECT_PATHS.has(location.pathname)) {
        setHasRedirected(true);
        dispatch(setIsComingFromNonUserRoute(false));
        return;
      }

      // Signed in from the PDF downsell link (see PdfOffer): finish what the link started.
      if (hasPendingPdfOffer()) {
        setHasRedirected(true);
        dispatch(setIsComingFromNonUserRoute(false));
        navigate('/pdf-offer', { replace: true });
        return;
      }

      // An Etsy buyer's destination is already decided, by the claim page, from
      // whether THIS order has a book yet. The smart redirect asks a different
      // question — "does this account have books?" — and for a repeat buyer it
      // answers /dashboard, dropping them on My Books (a Shopify library screen
      // with Shopify buttons) instead of the book they just paid for. Their
      // account is keyed on the Etsy buyer id, so a second order is always a
      // repeat visit with the previous order's books already on it.
      if (isEtsySession()) {
        setHasRedirected(true);
        dispatch(setIsComingFromNonUserRoute(false));
        return;
      }

      // A gift book being started from the gift page (?gift= on /create-book):
      // the smart redirect would drop the param and with it which gift the
      // new book belongs to.
      if (giftIdFromSearch(location.search)) {
        setHasRedirected(true);
        dispatch(setIsComingFromNonUserRoute(false));
        return;
      }

      // Keep the purchase-format tag across the smart redirect. It is not on the
      // book until the customer names it, so dropping it here silently converts a
      // PDF buyer to the physical SKU.
      const bookTag = currentUrlBookTag();
      // A fresh visit whose book is already in the cart lands on Finalize Order. Not for a reload
      // inside a book's steps (?stepCode=): someone editing an in-cart book stays in the book.
      // The cart is read from the server when this browser has none (new device).
      const isStepPage = !!new URLSearchParams(location.search).get('stepCode');
      setHasRedirected(true);
      (async () => {
        const cartBookIds = isStepPage ? undefined : await loadCartBookIds();
        const destination = withBookTag(
          determineUserDestination(
            userData.profile,
            userData.myBooks,
            userData.activeBookId,
            { cartBookIds }
          ),
          bookTag
        );

        console.log('UserRoute redirecting to:', destination);
        navigate(destination, { replace: true });
      })();
      dispatch(setIsComingFromNonUserRoute(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location.pathname,
    session?.user?._id,
    !!userData,
    hasRedirected,
    booksLoading,
    isComingFromNonUserRoute
  ]);

  // Socket management with RunPod event listeners
  useEffect(() => {
    let socket;

    if (session?.user?._id && session?.user?.role === "user") {
      const token = localStorage.getItem('auth_token') || session?.user?.accessToken;

      if (!token) {
        console.error('No token found for socket authentication');
        return;
      }

      socket = io(SOCKET_URL, { auth: { token } });

      // Connection events
      socket.on('connect', () => {
        console.log(' Socket connected in UserRoute:', socket.id);
      });

      socket.on('disconnect', () => {
        console.log(' Socket disconnected from UserRoute');
      });

      socket.on('connect_error', (error) => {
        console.error(' Socket connection error:', error.message);
      });

      // ========================================
      // 🆕 COVER WORKFLOW EVENTS (with title)
      // ========================================

      socket.on('runpod:cover:started', (data) => {
        console.log(' Cover job started:', data);

        if (data.bookId === activeBookId) {
          //  Initialize cover job tracking
          coverJobRef.current = {
            isActive: true,
            jobId: data.jobId,
            expectedTotal: data.totalImages || 1, // Usually 1 for cover
            receivedCount: 0
          };

          dispatch(setCoverImageUploadStatus(true));
          console.log(` Tracking cover job: ${data.totalImages} image(s)`);
        }
      });

      socket.on('runpod:cover:progress', (data) => {
        console.log(' Cover progress:', data);

        if (data.bookId === activeBookId) {
          dispatch(setCoverImageUploadStatus(true));
          const { currentStage, overallPercentage } = data;
          console.log(`Cover Progress: ${overallPercentage}% - ${currentStage}`);
        }
      });

      //  Listen for individual cover image completions
      socket.on('runpod:cover:image_ready', (data) => {
        console.log('️ Individual cover image ready in UserRoute:', data);

        if (data.bookId === activeBookId) {
          fetchBatchImagesByBookId(activeBookId).then(result => {
            const coverImageData = result?.docs?.data?.filter(
              (image) => !!image.isCover
            ) || [];

            if (coverImageData[0]?.AIUrl) {
              dispatch(
                updateBook({
                  _id: activeBookId,
                  coverImage: coverImageData[0].AIUrl
                })
              );
              // AI-processed cover is now in DB — clear pending
              clearPendingCoverImage(activeBookId);
            }

            // Do not replace with [] on fetch errors or empty slices (wipes good Redux/socket state).
            if (coverImageData.length > 0) {
              dispatch(setCoverImages(coverImageData));
              console.log(` Updated Redux with cover image`);
            }

            //  REMOVED: Do NOT clear status here - wait for completed event

          }).catch(error => {
            console.error(' Failed to refetch cover after individual image:', error);
          });
        }
      });

      socket.on('runpod:cover:completed', (data) => {
        console.log(' Cover job completed:', data);

        if (data.bookId === activeBookId) {
          fetchBatchImagesByBookId(activeBookId).then(result => {
            const coverImageData = result?.docs?.data?.filter(
              (image) => !!image.isCover
            ) || [];

            if (!!coverImageData[0]?.AIUrl) {
              dispatch(
                updateBook({
                  _id: activeBookId,
                  coverImage: coverImageData[0].AIUrl
                })
              )
            }

            if (coverImageData.length > 0) {
              // AI-processed cover is now in DB — clear localStorage pending cover
              clearPendingCoverImage(activeBookId);
              dispatch(setCoverImages(coverImageData));
            }
          }).finally(() => {
            //  ONLY clear status in completed event
            dispatch(setCoverImageUploadStatus(false));
            dispatch(setImageUploadStatus(false));
            coverJobRef.current = {
              isActive: false,
              jobId: null,
              expectedTotal: 0,
              receivedCount: 0
            };
            console.log(' Cover completed event - status cleared');

            // Always refresh the image list on the review page when cover completes,
            // so the cover appears immediately without needing navigation.
            // Also bump lineart progress to 5% if lineart is processing and still at 0%.
            dispatch(setForceBookRefresh(1));
            const bookProgress = lineartProgressRef.current?.[activeBookId];
            if (bookProgress?.isProcessing && bookProgress?.overallPercentage === 0) {
              dispatch(setLineartProgressByBook({
                bookId: activeBookId,
                overallPercentage: 5,
              }));
              console.log('Cover completed — bumped lineart progress to 5%');
            }
            console.log('Cover completed — triggered image refresh on review page');
          });
        } else {
          // Not the active book — just update its coverImage in Redux
          fetchBatchImagesByBookId(data.bookId).then(result => {
            const coverImg = result?.docs?.data?.find((img) => !!img.isCover);
            if (coverImg?.AIUrl) {
              dispatch(updateBook({ _id: data.bookId, coverImage: coverImg.AIUrl }));
            }
          }).catch((err) => console.error('Failed to fetch cover for book:', err));
        }
      });

      socket.on('runpod:cover:failed', (data) => {
        console.error(' Cover job failed:', data);

        if (data.bookId === activeBookId) {
          dispatch(setCoverImageUploadStatus(false));
          dispatch(setImageUploadStatus(false)); //  ADDED
          coverJobRef.current = {
            isActive: false,
            jobId: null,
            expectedTotal: 0,
            receivedCount: 0
          };
        }
      });

      socket.on('runpod:cover:error', (data) => {
        console.error(' Cover error:', data);

        if (data.bookId === activeBookId) {
          dispatch(setCoverImageUploadStatus(false));
          dispatch(setImageUploadStatus(false)); //  ADDED
          coverJobRef.current = {
            isActive: false,
            jobId: null,
            expectedTotal: 0,
            receivedCount: 0
          };
        }
      });

      socket.on('runpod:cover:timeout', (data) => {
        console.error('⏱️ Cover job timeout:', data);

        if (data.bookId === activeBookId) {
          dispatch(setCoverImageUploadStatus(false));
          dispatch(setImageUploadStatus(false)); //  ADDED
          coverJobRef.current = {
            isActive: false,
            jobId: null,
            expectedTotal: 0,
            receivedCount: 0
          };
        }
      });

      // ========================================
      // 🆕 LINEART WORKFLOW EVENTS (no title)
      // ========================================

      socket.on('runpod:lineart:started', (data) => {
        console.log(' Lineart job started:', data);

        if (data.bookId === activeBookId) {
          // Full interior grid size for this run (chunk-independent). Prefer the
          // server's explicit interior count; data.totalImages is now also the
          // run-wide count (no longer a per-worker chunk size).
          const gridCount =
            Number(data.expectedInteriorCount) || Number(data.totalImages) || 0;

          //  Initialize lineart job tracking
          lineartJobRef.current = {
            isActive: true,
            jobId: data.jobId,
            expectedTotal: gridCount,
            receivedCount: 0
          };

          dispatch(
            setLineartProgressByBook({
              isProcessing: true,
              currentImage: 0,
              ...(gridCount ? { totalImages: gridCount } : {}),
              overallPercentage: 0,
              currentStage: 'Starting',
              completedImages: 0,
              bookId: data.bookId
            })
          )
          if (gridCount) {
            // grow-only: a per-chunk started event must never shrink the grid mid-run
            setExpectedInteriorPageCount(data.bookId, gridCount, { growOnly: true });
          }
          dispatch(setImageBatchUploadStatus(true));
          console.log(` Tracking lineart job: ${gridCount} interior pages`);
        }
      });

      socket.on('runpod:lineart:progress', (data) => {
        console.log(' Lineart progress event:', data);

        if (data.bookId === activeBookId) {
          dispatch(setImageBatchUploadStatus(true));
          const prevProgress = lineartProgressRef.current?.[data.bookId] || {};
          const activeBook = myBooksRef.current.find(
            (book) => String(book._id) === String(data.bookId),
          );
          const resolvedTotalImages = resolveLineartProgressTotalImages(data.bookId, {
            incoming:
              data.totalImageCount ?? data.totalImages ?? data.expectedTotal,
            prevProgress,
            activeBook,
            lineartJobExpectedTotal: lineartJobRef.current?.expectedTotal,
          });
          const { currentStage, overallPercentage, currentImage } = data;
          console.log(
            `Lineart Progress: ${overallPercentage}% - ${currentStage} (${currentImage}/${resolvedTotalImages})`,
          );
        }

        const hasPending = hasImagesSentToRunPending(data.bookId);
        const prevProgress = lineartProgressRef.current?.[data.bookId] || {};
        const activeBook = myBooksRef.current.find(
          (book) => String(book._id) === String(data.bookId),
        );
        const resolvedTotalImages = resolveLineartProgressTotalImages(data.bookId, {
          incoming:
            data.totalImageCount ?? data.totalImages ?? data.expectedTotal,
          prevProgress,
          activeBook,
          lineartJobExpectedTotal: lineartJobRef.current?.expectedTotal,
        });

        dispatch(
          setLineartProgressByBook({
            bookId: data.bookId,
            // Only allow isProcessing to be false if we have no more pending images
            isProcessing: data.percentage < 100 || hasPending,
            currentImage: data.currentImage ?? 0,
            ...(resolvedTotalImages > 0 ? { totalImages: resolvedTotalImages } : {}),
            overallPercentage: data.percentage ?? 0,
            currentStage: data.currentStage ?? data.stage ?? 'processing',
            completedImages: data.completedImages ?? data.currentLineartCount ?? 0,
          })
        )
      });

      socket.on('runpod:lineart:batch_completed', (data) => {
        console.log(' Lineart batch completed event:', data);

        const hasPending = hasImagesSentToRunPending(data.bookId);

        dispatch(
          setLineartProgressByBook({
            overallPercentage: data.percentage,
            completedImages: data.totalProcessed ?? undefined,
            // Only allow isProcessing to be false if we have no more pending images
            isProcessing: data.percentage < 100 || hasPending,
            currentStage: (data.isComplete && data.percentage === 100 && !hasPending)
              ? 'Completed'
              : undefined,
            bookId: data.bookId
          })
        )
      })

      //  Listen for individual lineart image completions
      socket.on('runpod:lineart:image_ready', (data) => {
        console.log('️ Individual lineart image ready in UserRoute:', data);

        if (data.bookId === activeBookId) {
          console.log(`[IMAGE_DEBUG] runpod:lineart:image_ready event for book: ${activeBookId}`);
          const seq = ++fetchSequenceRef.current;
          Promise.all([
            fetchOrderedInteriorImages(activeBookId),
            fetchBatchImagesByBookId(activeBookId),
          ])
            .then(([merged, result]) => {
              if (seq !== fetchSequenceRef.current) return;

              dispatch(setImages(merged));
              const batchRows = result?.docs?.data || [];
              const activeBook = myBooksRef.current.find(
                (book) => String(book._id) === String(data.bookId),
              );
              const lineartProgress = lineartProgressRef.current?.[data.bookId];
              const finishedCount = countFinishedInteriorFromBatchRows(batchRows);

              console.log(
                `[IMAGE_DEBUG] Updated Redux with ${merged.length} interior image(s); finished line art: ${finishedCount}`,
              );

              if (
                stagingUploadEnabledRef.current &&
                shouldClearLineartLocalStorage(data.bookId, batchRows, {
                  activeBook,
                  lineartProgress,
                  useStagingCountCheck: true,
                })
              ) {
                console.log(
                  ' [IMAGE_DEBUG] All expected illustrations ready — clearing localStorage',
                );
                clearPendingImages(activeBookId);
                clearExpectedInteriorPageCount(activeBookId);
              } else {
                console.log(
                  ' [IMAGE_DEBUG] Illustration still in progress — keeping pending storage.',
                );
              }
            })
            .catch((error) => {
              console.error(' Failed to refetch after individual image:', error);
            });
        }
      });

      socket.on('runpod:lineart:completed', (data) => {
        console.log(' Lineart job completed event received:', data);

        if (data.bookId === activeBookId) {
          console.log(`[IMAGE_DEBUG] runpod:lineart:completed event for book: ${activeBookId}`);
          if ((data.incompletePages ?? 0) > 0) {
            console.log(
              `[IMAGE_DEBUG] Lineart completed with ${data.incompletePages} incomplete page(s) — keeping local run state`,
            );
          }
          Promise.all([
            fetchOrderedInteriorImages(activeBookId),
            fetchBatchImagesByBookId(activeBookId),
          ])
            .then(([merged, result]) => {
              const batchRows = result?.docs?.data || [];
              const activeBook = myBooksRef.current.find(
                (book) => String(book._id) === String(activeBookId),
              );
              const lineartProgress = lineartProgressRef.current?.[activeBookId];
              const finishedCount = countFinishedInteriorFromBatchRows(batchRows);

              console.log(
                `[IMAGE_DEBUG] Completion check: finished line art: ${finishedCount}`,
              );

              if (
                (data.incompletePages ?? 0) === 0 &&
                shouldClearLineartLocalStorage(activeBookId, batchRows, {
                  activeBook,
                  lineartProgress,
                  useStagingCountCheck: stagingUploadEnabledRef.current,
                })
              ) {
                console.log(
                  ' All images processed — clearing status and localStorage',
                );
                markLineartRunFinished(dispatch, activeBookId, merged);

                lineartJobRef.current = {
                  isActive: false,
                  jobId: null,
                  expectedTotal: 0,
                  receivedCount: 0,
                };
              } else {
                console.log(
                  ' Worker finished but illustrations are not all ready. Keeping status active.',
                );
                dispatch(setImages(merged));
              }
            })
            .catch((err) => {
              console.error(' Failed to refetch on completed event:', err);
            });
        }
      });

      socket.on('runpod:lineart:failed', (data) => {
        console.error(' Lineart job failed:', data);

        if (data.bookId === activeBookId) {
          dispatch(setImageBatchUploadStatus(false));
          dispatch(clearUploadingImages());
          dispatch(setImageUploadStatus(false)); //  ADDED
          dispatch(
            setLineartProgressByBook({
              bookId: data.bookId,
              isProcessing: false,
              overallPercentage: data.percentage ?? 0,
            }),
          );
          lineartJobRef.current = {
            isActive: false,
            jobId: null,
            expectedTotal: 0,
            receivedCount: 0
          };
        }
      });

      socket.on('runpod:lineart:error', (data) => {
        console.error(' Lineart error:', data);

        if (data.bookId === activeBookId) {
          dispatch(setImageBatchUploadStatus(false));
          dispatch(clearUploadingImages());
          dispatch(setImageUploadStatus(false)); //  ADDED
          dispatch(
            setLineartProgressByBook({
              bookId: data.bookId,
              isProcessing: false,
              overallPercentage: data.percentage ?? 0,
            }),
          );
          lineartJobRef.current = {
            isActive: false,
            jobId: null,
            expectedTotal: 0,
            receivedCount: 0
          };
        }
      });

      socket.on('runpod:lineart:timeout', (data) => {
        console.error('⏱️ Lineart job timeout:', data);

        if (data.bookId === activeBookId) {
          dispatch(setImageBatchUploadStatus(false));
          dispatch(clearUploadingImages());
          dispatch(setImageUploadStatus(false)); //  ADDED
          dispatch(
            setLineartProgressByBook({
              bookId: data.bookId,
              isProcessing: false,
              overallPercentage: data.percentage ?? 0,
            }),
          );
          lineartJobRef.current = {
            isActive: false,
            jobId: null,
            expectedTotal: 0,
            receivedCount: 0
          };
        }
      });

      // ========================================
      // LEGACY GENERIC EVENTS (backward compatibility)
      // ========================================

      socket.on('runpod:started', (data) => {
        console.log(' RunPod job started (generic):', data);

        if (data.bookId === activeBookId) {
          if (data.workflowType === 'cover') {
            dispatch(setCoverImageUploadStatus(true));
          } else {
            dispatch(setImageBatchUploadStatus(true));
          }
        }
      });

      socket.on('runpod:progress', (data) => {
        console.log(' RunPod progress (generic):', data);

        if (data.bookId === activeBookId) {
          if (data.workflowType === 'cover') {
            dispatch(setCoverImageUploadStatus(true));
          } else {
            dispatch(setImageBatchUploadStatus(true));
          }
        }

        // Cover generation must never seed line art progress — doing so flips
        // the Choose Photos lock on while only the cover is rendering. Track
        // line art progress only for non-cover jobs.
        if (data.workflowType !== 'cover') {
          const hasPending = hasImagesSentToRunPending(data.bookId);
          const prevProgress = lineartProgressRef.current?.[data.bookId] || {};
          const activeBook = myBooksRef.current.find(
            (book) => String(book._id) === String(data.bookId),
          );
          const resolvedTotalImages = resolveLineartProgressTotalImages(data.bookId, {
            incoming: data.totalImages,
            prevProgress,
            activeBook,
            lineartJobExpectedTotal: lineartJobRef.current?.expectedTotal,
          });

          dispatch(
            setLineartProgressByBook({
              bookId: data.bookId,
              isProcessing: data.percentage < 100 || hasPending,
              currentImage: data.currentImage ?? 0,
              ...(resolvedTotalImages > 0 ? { totalImages: resolvedTotalImages } : {}),
              overallPercentage: data.percentage ?? 0,
              currentStage:
                data.currentStage ?? 'processing',
              completedImages:
                data.completedImages ?? 0,
            })
          )
        }
      });

      socket.on('runpod:completed', (data) => {
        console.log(' RunPod job completed (generic):', data);

        if (data.bookId === activeBookId) {
          fetchBatchImagesByBookId(activeBookId).then(result => {
            if (data.workflowType === 'cover') {
              const coverImageData = result?.docs?.data?.filter(
                (image) => !!image.isCover
              ) || [];
              if (coverImageData.length > 0) {
                clearPendingCoverImage(activeBookId);
                dispatch(setCoverImages(coverImageData));
              }
              dispatch(setCoverImageUploadStatus(false));
              dispatch(setImageUploadStatus(false));
            } else {
              Promise.all([
                fetchOrderedInteriorImages(activeBookId),
                fetchBatchImagesByBookId(activeBookId),
              ])
                .then(([merged, result]) => {
                  const batchRows = result?.docs?.data || [];
                  const activeBook = myBooksRef.current.find(
                    (book) => String(book._id) === String(activeBookId),
                  );
                  const lineartProgress = lineartProgressRef.current?.[activeBookId];
                  const finishedCount = countFinishedInteriorFromBatchRows(batchRows);

                  console.log(
                    `[IMAGE_DEBUG] Generic job completion check: finished line art: ${finishedCount}`,
                  );

                  if (
                    (data.incompletePages ?? 0) === 0 &&
                    shouldClearLineartLocalStorage(activeBookId, batchRows, {
                      activeBook,
                      lineartProgress,
                      useStagingCountCheck: stagingUploadEnabledRef.current,
                    })
                  ) {
                    console.log(
                      ' All images processed (generic) — clearing status and localStorage',
                    );
                    markLineartRunFinished(dispatch, activeBookId, merged);
                  } else {
                    console.log(
                      ' Worker finished (generic) but illustrations are not all ready. Keeping status active.',
                    );
                    dispatch(setImages(merged));
                  }
                })
                .catch((err) => {
                  console.error(
                    ' Failed to refetch on generic completed event:',
                    err,
                  );
                });
            }
          }).catch(err => {
            console.error(' Failed to refetch on generic completed event:', err);
          });
        }
      });

      socket.on('runpod:failed', (data) => {
        console.error(' RunPod job failed (generic):', data);

        if (data.bookId === activeBookId) {
          if (data.workflowType === 'cover') {
            dispatch(setCoverImageUploadStatus(false));
            dispatch(setImageUploadStatus(false)); //  ADDED
          } else {
            dispatch(setImageBatchUploadStatus(false));
            dispatch(clearUploadingImages());
            dispatch(setImageUploadStatus(false)); //  ADDED
          }
        }
      });

      socket.on('runpod:error', (data) => {
        console.error(' RunPod error (generic):', data);

        if (data.bookId === activeBookId) {
          if (data.workflowType === 'cover') {
            dispatch(setCoverImageUploadStatus(false));
            dispatch(setImageUploadStatus(false)); //  ADDED
          } else {
            dispatch(setImageBatchUploadStatus(false));
            dispatch(clearUploadingImages());
            dispatch(setImageUploadStatus(false)); //  ADDED
          }
        }
      });

      // A cover or pages ended without their drawing (API services/drawStatus.js):
      // mark them failed now so the failure screens show without a reload, and
      // let Preview refetch.
      socket.on('runpod:draw_failed', (data) => {
        if (data.bookId !== activeBookId) return;
        const failed = new Set((data.mediaIds || []).map(String));
        const markFailed = (img) =>
          failed.has(String(img._id || img.id))
            ? { ...img, drawStatus: 'failed', isGenerating: false, isLoading: false }
            : img;
        dispatch((dispatch, getState) => {
          const { coverImages, images } = getState().sketch;
          if (data.workflowType === 'cover') dispatch(setCoverImages(coverImages.map(markFailed)));
          else dispatch(setImages(images.map(markFailed)));
        });
        if (data.workflowType === 'cover') dispatch(setCoverImageUploadStatus(false));
        dispatch(setForceBookRefresh(1));
      });

      socket.on('runpod:timeout', (data) => {
        console.error('⏱️ RunPod job timeout (generic):', data);

        if (data.bookId === activeBookId) {
          if (data.workflowType === 'cover') {
            dispatch(setCoverImageUploadStatus(false));
            dispatch(setImageUploadStatus(false)); //  ADDED
          } else {
            dispatch(setImageBatchUploadStatus(false));
            dispatch(clearUploadingImages());
            dispatch(setImageUploadStatus(false)); //  ADDED
          }
        }
      });
    }

    return () => {
      if (socket) {
        // Remove all cover workflow listeners
        socket.off('runpod:cover:started');
        socket.off('runpod:cover:progress');
        socket.off('runpod:cover:image_ready');
        socket.off('runpod:cover:completed');
        socket.off('runpod:cover:failed');
        socket.off('runpod:draw_failed');
        socket.off('runpod:cover:error');
        socket.off('runpod:cover:timeout');

        // Remove all lineart workflow listeners
        socket.off('runpod:lineart:started');
        socket.off('runpod:lineart:progress');
        socket.off('runpod:lineart:image_ready');
        socket.off('runpod:lineart:completed');
        socket.off('runpod:lineart:failed');
        socket.off('runpod:lineart:error');
        socket.off('runpod:lineart:timeout');

        // Remove generic listeners
        socket.off('runpod:started');
        socket.off('runpod:progress');
        socket.off('runpod:completed');
        socket.off('runpod:failed');
        socket.off('runpod:error');
        socket.off('runpod:timeout');

        socket.disconnect();
        console.log(' Socket cleanup in UserRoute');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?._id, session?.user?.role, activeBookId, dispatch]);

  // Show loading state
  if (session?.status === 'loading' || isLoadingData || booksLoading || fetchingImages) {
    return <LoadingFallback />;
  }

  // Only render children if authorized user
  return session?.status === 'authorized' && session?.user?.role === 'user'
    ? children
    : null;
});

export default UserRoute;